import React, { useState, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppContext } from '../../context/AppContext';
import {
  Workflow,
  ShieldCheck,
  TrendingUp,
  Plus,
  Clock,
  CreditCard,
  Users,
  Smartphone,
  MessageSquare,
  Gift,
  CheckCircle2,
  XCircle,
  IndianRupee,
  AlertCircle,
  ShoppingBag,
  Target,
  PieChart,
  Trophy,
  Share2,
  Sparkles,
  ArrowLeft,
  Settings,
  Calendar,
  Star,
  Cake,
  ThumbsUp
} from 'lucide-react';
import '../../assets/css/Wallet.css';


export default function WelletPage() {
  const { sidebar } = useContext(AppContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'campaigns';

  const setActiveTab = (key) => setSearchParams({ tab: key });

  const renderContent = () => {
    switch (activeTab) {
      case 'campaigns':    return <CampaignSetup />;
      case 'workflows':    return <WorkflowEngine />;
      case 'gamification': return <GamificationEngine />;
      case 'permissions':  return <PermissionsControl />;
      case 'reporting':    return <StrategicReporting />;
      default:             return <CampaignSetup />;
    }
  };

  return (
    <div className={sidebar ? "taskMain" : "cx-active taskMain"} id="cx-main">
      {/* Page Header */}
      <div className="page-header d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center gap-2">
          <div className="bg-primary p-2 rounded-3 text-white d-flex align-items-center justify-content-center">
            <CreditCard size={20} />
          </div>
        </div>
        <span className="d-flex align-items-center gap-2 small" style={{ color: 'var(--text-secondary)' }}>
          <span className="rounded-circle bg-success d-inline-block" style={{ width: 8, height: 8 }}></span>
          System Operational
        </span>
      </div>

      {/* Content */}
      <div className="fade-in">
        {renderContent()}
      </div>
    </div>
  );
}

// ─── Screen 1: Campaigns ──────────────────────────────────────────
function CampaignSetup() {
  const [campaigns, setCampaigns] = useState([
    { id: 1, name: '3-Day App Nudge',        type: 'Acquisition',      value: '10',  discountType: 'Percentage Off', expValue: '48', expUnit: 'Hours', budget: '10000', maxBalance: '150', status: 'Active',   usage: 4500 },
    { id: 2, name: 'Apology Credits',         type: 'Service Recovery', value: '50',  discountType: 'Flat Amount',    expValue: '7',  expUnit: 'Days',  budget: '5000',  maxBalance: '100', status: 'Active',   usage: 4250 },
    { id: 3, name: 'Refer & Earn Bonus',      type: 'Acquisition',      value: '100', discountType: 'Flat Amount',    expValue: '30', expUnit: 'Days',  budget: '25000', maxBalance: '500', status: 'Inactive', usage: 0 },
    { id: 4, name: '5th Order Milestone',     type: 'Retention',        value: '15',  discountType: 'Percentage Off', expValue: '14', expUnit: 'Days',  budget: '15000', maxBalance: '300', status: 'Inactive', usage: 0 },
    { id: 5, name: 'Cart Abandonment Nudge',  type: 'Retention',        value: '5',   discountType: 'Percentage Off', expValue: '48', expUnit: 'Hours', budget: '8000',  maxBalance: '100', status: 'Inactive', usage: 0 },
    { id: 6, name: 'Dessert Cross-Sell',      type: 'Cross-Sell',       value: '5',   discountType: 'Percentage Off', expValue: '24', expUnit: 'Hours', budget: '5000',  maxBalance: '50',  status: 'Inactive', usage: 0 },
    { id: 7, name: 'Win-Back (30 Days)',      type: 'Retention',        value: '20',  discountType: 'Percentage Off', expValue: '7',  expUnit: 'Days',  budget: '12000', maxBalance: '200', status: 'Inactive', usage: 0 },
    { id: 8, name: 'Late Delivery Comp',      type: 'Service Recovery', value: '100', discountType: 'Flat Amount',    expValue: '30', expUnit: 'Days',  budget: '5000',  maxBalance: '200', status: 'Inactive', usage: 0 },
  ]);
  const [viewMode, setViewMode]               = useState('list');
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [formData, setFormData]               = useState({
    name: '', type: 'Acquisition', value: '', discountType: 'Flat Amount', expValue: '', expUnit: 'Hours', budget: '', maxBalance: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCreateSubmit = (e) => {
    e.preventDefault();
    setCampaigns([{ ...formData, id: Date.now(), status: 'Active', usage: 0 }, ...campaigns]);
    setViewMode('list');
    setFormData({ name: '', type: 'Acquisition', value: '', discountType: 'Flat Amount', expValue: '', expUnit: 'Hours', budget: '', maxBalance: '' });
  };

  const viewCampaign = (campaign) => { setSelectedCampaign(campaign); setViewMode('view'); };

  // ── Create Form ──
  if (viewMode === 'create') {
    return (
      <div className="d-flex flex-column gap-4">
        <div className="d-flex align-items-center gap-3 mb-2">
          <button onClick={() => setViewMode('list')} className="btn btn-light text-secondary">
            <ArrowLeft size={18} />
          </button>
          <div>
            <h5 className="fw-semibold mb-0" style={{ color: 'var(--text-primary)' }}>Create New Campaign</h5>
            <p className="small mb-0" style={{ color: 'var(--text-secondary)' }}>Configure rules and financial exposure for your new promotion.</p>
          </div>
        </div>
        <form onSubmit={handleCreateSubmit} className="card shadow-sm border-0 rounded-4 p-4">
          <h6 className="fw-semibold mb-4 pb-2 border-bottom" style={{ color: 'var(--text-primary)' }}>Campaign Setup</h6>
          <div className="row g-4">
            <div className="col-md-6 d-flex flex-column gap-3">
              <div>
                <label className="form-label">Campaign Name</label>
                <input required type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="e.g., 3-Day App Download Nudge" className="form-control" />
              </div>
              <div>
                <label className="form-label">Campaign Type</label>
                <select name="type" value={formData.type} onChange={handleInputChange} className="form-select">
                  <option value="Acquisition">Acquisition (New Users)</option>
                  <option value="Retention">Retention (Repeat Orders)</option>
                  <option value="Service Recovery">Service Recovery (Apologies)</option>
                  <option value="Cross-Sell">Cross-Sell (AOV Lift)</option>
                </select>
              </div>
              <div className="row g-3">
                <div className="col-6">
                  <label className="form-label">Credit Value</label>
                  <div className="input-group">
                    <span className="input-group-text">₹</span>
                    <input required type="number" name="value" value={formData.value} onChange={handleInputChange} className="form-control" />
                  </div>
                </div>
                <div className="col-6">
                  <label className="form-label">Type</label>
                  <select name="discountType" value={formData.discountType} onChange={handleInputChange} className="form-select">
                    <option value="Flat Amount">Flat Amount</option>
                    <option value="Percentage Off">Percentage Off</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="col-md-6 d-flex flex-column gap-3">
              <div>
                <label className="form-label">Expiration Rules</label>
                <div className="d-flex align-items-center gap-2">
                  <input required type="number" name="expValue" value={formData.expValue} onChange={handleInputChange} className="form-control" style={{ width: 100 }} />
                  <select name="expUnit" value={formData.expUnit} onChange={handleInputChange} className="form-select w-auto">
                    <option value="Hours">Hours</option>
                    <option value="Days">Days</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="form-label">Global Budget Cap</label>
                <div className="input-group">
                  <span className="input-group-text">₹</span>
                  <input required type="number" name="budget" value={formData.budget} onChange={handleInputChange} className="form-control" />
                </div>
                <div className="form-text">Campaign pauses automatically when total issued hits this limit.</div>
              </div>
              <div>
                <label className="form-label">Wallet Balance Limit (Max)</label>
                <div className="input-group">
                  <span className="input-group-text">₹</span>
                  <input required type="number" name="maxBalance" value={formData.maxBalance} onChange={handleInputChange} className="form-control" placeholder="Prevents promo-stacking" />
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4 pt-3 border-top d-flex justify-content-end gap-2">
            <button type="button" onClick={() => setViewMode('list')} className="btn btn-light fw-medium">Cancel</button>
            <button type="submit" className="btn btn-primary fw-medium px-4">Save Campaign</button>
          </div>
        </form>
      </div>
    );
  }

  // ── Detail View ──
  if (viewMode === 'view' && selectedCampaign) {
    const usagePercent = Math.min(100, Math.round((selectedCampaign.usage / selectedCampaign.budget) * 100)) || 0;
    return (
      <div className="d-flex flex-column gap-4">
        <div className="d-flex align-items-center gap-3 mb-2">
          <button onClick={() => setViewMode('list')} className="btn btn-light text-secondary">
            <ArrowLeft size={18} />
          </button>
          <div>
            <h5 className="fw-semibold mb-0" style={{ color: 'var(--text-primary)' }}>{selectedCampaign.name}</h5>
            <p className="small mb-0" style={{ color: 'var(--text-secondary)' }}>Campaign Configuration & Details</p>
          </div>
          <div className="ms-auto d-flex align-items-center gap-2">
            <span className={`badge rounded-pill px-3 py-2 ${selectedCampaign.status === 'Active' ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'}`}>
              {selectedCampaign.status}
            </span>
            <button className="btn btn-sm btn-outline-danger fw-medium px-3">Pause</button>
            <button className="btn btn-sm btn-primary fw-medium px-3">Edit</button>
          </div>
        </div>
        <div className="row g-4">
          <div className="col-lg-8">
            <div className="card shadow-sm border-0 rounded-4 p-4 h-100">
              <h6 className="fw-semibold mb-4 pb-3 border-bottom d-flex align-items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                <Settings size={16} className="text-primary" /> Configuration Settings
              </h6>
              <div className="row g-4">
                {[
                  { label: 'Campaign Type', value: selectedCampaign.type },
                  { label: 'Credit Value', value: selectedCampaign.discountType === 'Percentage Off' ? `${selectedCampaign.value}% Off` : `₹${selectedCampaign.value} Flat` },
                  { label: 'Expiration', value: `${selectedCampaign.expValue} ${selectedCampaign.expUnit}` },
                  { label: 'Max Wallet Balance', value: `₹${selectedCampaign.maxBalance}` },
                ].map(({ label, value }) => (
                  <div className="col-sm-6" key={label}>
                    <p className="fw-bold text-uppercase mb-1" style={{ fontSize: '0.7rem', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>{label}</p>
                    <p className="small fw-medium mb-0" style={{ color: 'var(--text-primary)' }}>{value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="col-lg-4">
            <div className="card shadow-sm border-0 rounded-4 p-4 h-100 d-flex flex-column">
              <h6 className="fw-semibold mb-4 pb-3 border-bottom" style={{ color: 'var(--text-primary)' }}>Budget Utilization</h6>
              <div className="flex-grow-1 d-flex flex-column justify-content-center">
                <div className="d-flex justify-content-between align-items-end mb-2">
                  <span className="fs-4 fw-bold" style={{ color: 'var(--text-primary)' }}>₹{selectedCampaign.usage.toLocaleString()}</span>
                  <span className="small" style={{ color: 'var(--text-secondary)' }}>/ ₹{Number(selectedCampaign.budget).toLocaleString()}</span>
                </div>
                <div className="progress mb-2" style={{ height: '0.5rem' }}>
                  <div className={`progress-bar ${usagePercent > 90 ? 'bg-danger' : usagePercent > 75 ? 'bg-warning' : 'bg-primary'}`} style={{ width: `${usagePercent}%` }}></div>
                </div>
                <p className="small text-end mb-0" style={{ color: 'var(--text-secondary)' }}>{usagePercent}% Used</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── List View ──
  const typeColors = {
    'Acquisition':      'bg-info-subtle text-info-emphasis',
    'Retention':        'bg-primary-subtle text-primary-emphasis',
    'Service Recovery': 'bg-danger-subtle text-danger-emphasis',
    'Cross-Sell':       'bg-warning-subtle text-warning-emphasis',
  };

  return (
    <div className="d-flex flex-column gap-4">
      <div className="d-flex justify-content-between align-items-center">
        <div>
          <h5 className="fw-semibold mb-0" style={{ color: 'var(--text-primary)' }}>Wallet Campaigns</h5>
          <p className="small mb-0" style={{ color: 'var(--text-secondary)' }}>Manage promotional rules and financial exposure.</p>
        </div>
        <button onClick={() => setViewMode('create')} className="btn btn-primary d-flex align-items-center gap-2 fw-medium">
          <Plus size={16} /> Create Campaign
        </button>
      </div>
      <div className="data-table-wrapper">
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead>
              <tr>
                {['Campaign Name', 'Type', 'Value', 'Budget Usage', 'Status', 'Action'].map(h => (
                  <th key={h} className={h === 'Action' ? 'text-end' : ''}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {campaigns.map((camp) => {
                const pct = Math.min(100, Math.round((camp.usage / camp.budget) * 100)) || 0;
                return (
                  <tr key={camp.id}>
                    <td className="fw-medium" style={{ color: 'var(--text-primary)' }}>{camp.name}</td>
                    <td>
                      <span className={`badge rounded px-2 py-1 fw-normal ${typeColors[camp.type] || 'bg-secondary-subtle text-secondary'}`}>
                        {camp.type}
                      </span>
                    </td>
                    <td>{camp.discountType === 'Percentage Off' ? `${camp.value}% Off` : `₹${camp.value} Flat`}</td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <div className="progress flex-grow-1" style={{ height: '0.4rem', width: 80 }}>
                          <div className={`progress-bar ${pct > 90 ? 'bg-danger' : pct > 75 ? 'bg-warning' : 'bg-primary'}`} style={{ width: `${pct}%` }}></div>
                        </div>
                        <span className="small" style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>{pct}%</span>
                      </div>
                    </td>
                    <td>
                      <span className={`badge rounded-pill px-2 py-1 fw-medium ${camp.status === 'Active' ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'}`}>
                        {camp.status}
                      </span>
                    </td>
                    <td className="text-end">
                      <button onClick={() => viewCampaign(camp)} className="btn btn-link text-primary text-decoration-none fw-medium p-0" style={{ fontSize: '0.8rem' }}>
                        View Details
                      </button>
                    </td>
                  </tr>
                );
              })}
              {campaigns.length === 0 && (
                <tr><td colSpan="6" className="text-center py-5" style={{ color: 'var(--text-secondary)' }}>No campaigns found. Create one to get started.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Screen 2: Workflows ──────────────────────────────────────────
function WorkflowEngine() {
  const [selectedWorkflow, setSelectedWorkflow] = useState('Phone to App');

  const workflowOptions = [
    { value: 'Phone to App', label: 'Phone to App Conversion' },
    { value: 'Cart Abandon',  label: 'Cart Abandonment' },
    { value: 'Win-Back',      label: '30-Day Win-Back' },
    { value: 'Second Visit',  label: 'Second Visit Guarantee' },
    { value: 'Feedback',      label: 'Post-Order Feedback' },
    { value: 'Birthday',      label: 'Birthday Reward' },
  ];

  return (
    <div className="d-flex flex-column gap-4">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
        <div>
          <h5 className="fw-semibold mb-0" style={{ color: 'var(--text-primary)' }}>Automated "Nudge" Workflows</h5>
          <p className="small mb-0" style={{ color: 'var(--text-secondary)' }}>Design behavior-driven triggers and automated actions.</p>
        </div>
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <div className="d-flex gap-2">
            <select value={selectedWorkflow} onChange={(e) => setSelectedWorkflow(e.target.value)} className="form-select bg-white" style={{ minWidth: 260 }}>
              {workflowOptions.map(o => <option key={o.value} value={o.value}>Workflow: {o.label}</option>)}
            </select>
            <button className="btn btn-outline-secondary fw-medium bg-white">Edit</button>
          </div>
          <div className="form-check form-switch m-0 d-flex align-items-center gap-2">
            <input className="form-check-input mt-0" type="checkbox" role="switch" id="workflowToggle" defaultChecked style={{ width: '2.5rem', height: '1.25rem' }} />
            <label className="form-check-label small fw-bold text-success mb-0" htmlFor="workflowToggle">ACTIVE</label>
          </div>
        </div>
      </div>

      <div className="card shadow-sm border-0 rounded-4 p-4 p-md-5 d-flex flex-column align-items-center" style={{ minHeight: 600 }}>
        <WorkflowDiagram type={selectedWorkflow} />
      </div>
    </div>
  );
}

function WorkflowDiagram({ type }) {
  const Connector = () => (
    <div className="border-start border-secondary" style={{ height: '2rem', width: 1 }}></div>
  );
  const TimerBadge = ({ label }) => (
    <div className="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle rounded-pill px-3 py-2 d-flex align-items-center gap-1">
      <Clock size={14} /> {label}
    </div>
  );
  const ConditionCard = ({ label }) => (
    <div className="card bg-dark text-white rounded-4 p-3 shadow-sm w-100" style={{ maxWidth: '28rem' }}>
      <div className="d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center gap-2">
          <Workflow size={20} className="text-secondary" />
          <span className="fw-medium">Condition Check</span>
        </div>
        <span className="badge bg-secondary rounded-pill fw-normal px-2 py-1">{label}</span>
      </div>
    </div>
  );
  const YesNoSplit = () => (
    <div className="d-flex w-100 justify-content-center position-relative" style={{ maxWidth: '28rem' }}>
      <div className="w-50 border-top border-start border-secondary mt-4 position-relative" style={{ height: '2rem', borderTopLeftRadius: '0.5rem' }}>
        <div className="position-absolute top-0 start-0 translate-middle bg-white px-2 small fw-bold text-success d-flex align-items-center gap-1"><CheckCircle2 size={14}/> YES</div>
      </div>
      <div className="w-50 border-top border-end border-secondary mt-4 position-relative" style={{ height: '2rem', borderTopRightRadius: '0.5rem' }}>
        <div className="position-absolute top-0 end-0 translate-middle bg-white px-2 small fw-bold text-danger d-flex align-items-center gap-1"><XCircle size={14}/> NO</div>
      </div>
    </div>
  );
  const EndCard = ({ label }) => (
    <div className="flex-grow-1 card border rounded-4 p-3 shadow-sm text-center d-flex flex-column align-items-center justify-content-center" style={{ maxWidth: 280, minHeight: 120, opacity: 0.7 }}>
      <CheckCircle2 size={24} className="text-secondary mb-2" />
      <h6 className="fw-medium mb-1" style={{ color: 'var(--text-primary)' }}>End Workflow</h6>
      <p className="small mb-0" style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{label}</p>
    </div>
  );
  const NudgeCard = ({ credit, expires, sms }) => (
    <div className="flex-grow-1 card border-success-subtle bg-success-subtle rounded-4 p-3 shadow-sm position-relative" style={{ maxWidth: 320 }}>
      <div className="position-absolute top-0 start-50 translate-middle bg-success text-white fw-bold px-2 py-1 rounded small text-uppercase shadow-sm" style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}>
        The Nudge
      </div>
      <div className="d-flex flex-column gap-3 mt-3">
        <div className="d-flex align-items-start gap-2">
          <Gift size={18} className="text-success flex-shrink-0 mt-1" />
          <div>
            <h6 className="small fw-semibold mb-0" style={{ color: 'var(--text-primary)' }}>{credit}</h6>
            <p className="mb-0" style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>{expires}</p>
          </div>
        </div>
        <div className="d-flex align-items-start gap-2 pt-2 border-top border-success-subtle">
          <MessageSquare size={18} className="text-success flex-shrink-0 mt-1" />
          <div>
            <h6 className="small fw-semibold mb-0" style={{ color: 'var(--text-primary)' }}>Send Automated SMS</h6>
            <div className="bg-white border border-success-subtle p-2 rounded mt-1 fst-italic" style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>{sms}</div>
          </div>
        </div>
      </div>
    </div>
  );

  if (type === 'Phone to App') return (
    <>
      <div className="card border-primary-subtle bg-primary-subtle rounded-4 p-3 shadow-sm w-100" style={{ maxWidth: '28rem' }}>
        <div className="d-flex align-items-start gap-3">
          <div className="bg-primary p-2 rounded-circle text-white mt-1"><Smartphone size={20} /></div>
          <div>
            <span className="small fw-bold text-primary text-uppercase" style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}>Trigger Event</span>
            <h6 className="fw-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Phone order placed via Agent App</h6>
            <p className="small mb-0" style={{ color: 'var(--text-secondary)' }}>Customer calls in and agent places order under their phone number.</p>
          </div>
        </div>
      </div>
      <Connector />
      <div className="card border rounded-4 p-3 shadow-sm w-100" style={{ maxWidth: '28rem' }}>
        <div className="d-flex align-items-start gap-3">
          <div className="bg-light p-2 rounded-circle text-secondary mt-1"><MessageSquare size={20} /></div>
          <div>
            <span className="small fw-bold text-secondary text-uppercase" style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}>Immediate Action</span>
            <h6 className="fw-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Send standard SMS link</h6>
            <div className="bg-light border p-2 rounded-2 mt-2 fst-italic" style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
              "Thanks for ordering! Track your order and reorder easily by downloading our app: [Link]"
            </div>
          </div>
        </div>
      </div>
      <Connector /><TimerBadge label="Wait 3 Days" /><Connector />
      <ConditionCard label="Has user logged into App?" />
      <YesNoSplit />
      <div className="d-flex w-100 justify-content-center gap-4" style={{ maxWidth: '42rem' }}>
        <EndCard label="Customer successfully converted." />
        <NudgeCard credit="Deposit 10% Discount Credit" expires="Applies to phone-linked wallet profile. Expires in 48h." sms='"Get 10% off your next meal if you order via the app today! Click here: [Link]"' />
      </div>
    </>
  );

  if (type === 'Cart Abandon') return (
    <>
      <div className="card border-info-subtle bg-info-subtle rounded-4 p-3 shadow-sm w-100" style={{ maxWidth: '28rem' }}>
        <div className="d-flex align-items-start gap-3">
          <div className="bg-info text-white p-2 rounded-circle mt-1"><ShoppingBag size={20} /></div>
          <div>
            <span className="small fw-bold text-info-emphasis text-uppercase" style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}>Trigger Event</span>
            <h6 className="fw-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Items left in Cart</h6>
            <p className="small mb-0" style={{ color: 'var(--text-secondary)' }}>User added items to their basket but exited the app without checking out.</p>
          </div>
        </div>
      </div>
      <Connector />
      <div className="card border rounded-4 p-3 shadow-sm w-100" style={{ maxWidth: '28rem' }}>
        <div className="d-flex align-items-start gap-3">
          <div className="bg-light p-2 rounded-circle text-secondary mt-1"><MessageSquare size={20} /></div>
          <div>
            <span className="small fw-bold text-secondary text-uppercase" style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}>Immediate Action</span>
            <h6 className="fw-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Send standard Push Notification</h6>
            <div className="bg-light border p-2 rounded-2 mt-2 fst-italic" style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
              "You left something delicious in your cart! Tap to complete your order before it gets cold."
            </div>
          </div>
        </div>
      </div>
      <Connector /><TimerBadge label="Wait 3 Days" /><Connector />
      <ConditionCard label="Has user completed a purchase?" />
      <YesNoSplit />
      <div className="d-flex w-100 justify-content-center gap-4" style={{ maxWidth: '42rem' }}>
        <EndCard label="Customer completed checkout organically." />
        <NudgeCard credit="Deposit 5% Discount Credit" expires="Applies to their wallet profile. Expires in 48h." sms='"Still hungry? We added a 5% off credit to your wallet to help you finish your order today! Tap here to checkout."' />
      </div>
    </>
  );

  if (type === 'Win-Back') return (
    <>
      <div className="card border-primary-subtle bg-primary-subtle rounded-4 p-3 shadow-sm w-100" style={{ maxWidth: '28rem' }}>
        <div className="d-flex align-items-start gap-3">
          <div className="bg-primary p-2 rounded-circle text-white mt-1"><Calendar size={20} /></div>
          <div>
            <span className="small fw-bold text-primary text-uppercase" style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}>Trigger Event</span>
            <h6 className="fw-semibold mb-1" style={{ color: 'var(--text-primary)' }}>30 Days Inactive</h6>
            <p className="small mb-0" style={{ color: 'var(--text-secondary)' }}>Customer has not placed an order or engaged with the app in 30 days.</p>
          </div>
        </div>
      </div>
      <Connector />
      <ConditionCard label="Has user opened app recently?" />
      <YesNoSplit />
      <div className="d-flex w-100 justify-content-center gap-4" style={{ maxWidth: '42rem' }}>
        <EndCard label="User is still browsing, hold promotion." />
        <NudgeCard credit="Deposit 20% Win-Back Credit" expires="High-value incentive. Expires in 7 Days." sms={"\"It's been a while! We've added a special 20% discount to your wallet. Come back and treat yourself this week!\""} />
      </div>
    </>
  );

  if (type === 'Second Visit') return (
    <>
      <div className="card border-warning-subtle bg-warning-subtle rounded-4 p-3 shadow-sm w-100" style={{ maxWidth: '28rem' }}>
        <div className="d-flex align-items-start gap-3">
          <div className="bg-warning text-dark p-2 rounded-circle mt-1"><Star size={20} /></div>
          <div>
            <span className="small fw-bold text-warning-emphasis text-uppercase" style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}>Trigger Event</span>
            <h6 className="fw-semibold mb-1" style={{ color: 'var(--text-primary)' }}>First App Order Completed</h6>
            <p className="small mb-0" style={{ color: 'var(--text-secondary)' }}>Customer has successfully received their very first order via the App.</p>
          </div>
        </div>
      </div>
      <Connector /><TimerBadge label="Wait 24 Hours" /><Connector />
      <div className="card border-success-subtle bg-success-subtle rounded-4 p-3 shadow-sm position-relative w-100" style={{ maxWidth: '28rem' }}>
        <div className="position-absolute top-0 start-50 translate-middle bg-success text-white fw-bold px-2 py-1 rounded small text-uppercase shadow-sm" style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}>Habit Builder</div>
        <div className="d-flex flex-column gap-3 mt-3">
          <div className="d-flex align-items-start gap-2">
            <Gift size={18} className="text-success flex-shrink-0 mt-1" />
            <div>
              <h6 className="small fw-semibold mb-0" style={{ color: 'var(--text-primary)' }}>Deposit ₹50 Flat Credit</h6>
              <p className="mb-0" style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>Guarantees a 2nd visit. Expires in 7 Days.</p>
            </div>
          </div>
          <div className="d-flex align-items-start gap-2 pt-2 border-top border-success-subtle">
            <MessageSquare size={18} className="text-success flex-shrink-0 mt-1" />
            <div>
              <h6 className="small fw-semibold mb-0" style={{ color: 'var(--text-primary)' }}>Send Thank You SMS/Push</h6>
              <div className="bg-white border border-success-subtle p-2 rounded mt-1 fst-italic" style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                "Thanks for trying our app! Here is ₹50 on us for your next meal. Valid for the next 7 days."
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  if (type === 'Feedback') return (
    <>
      <div className="card border-info-subtle bg-info-subtle rounded-4 p-3 shadow-sm w-100" style={{ maxWidth: '28rem' }}>
        <div className="d-flex align-items-start gap-3">
          <div className="bg-info text-white p-2 rounded-circle mt-1"><ShoppingBag size={20} /></div>
          <div>
            <span className="small fw-bold text-info-emphasis text-uppercase" style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}>Trigger Event</span>
            <h6 className="fw-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Order Marked "Delivered"</h6>
            <p className="small mb-0" style={{ color: 'var(--text-secondary)' }}>Delivery partner confirms drop-off.</p>
          </div>
        </div>
      </div>
      <Connector /><TimerBadge label="Wait 2 Hours" /><Connector />
      <div className="card bg-dark text-white rounded-4 p-3 shadow-sm w-100" style={{ maxWidth: '28rem' }}>
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-2"><Workflow size={20} className="text-secondary" /><span className="fw-medium">Condition Check</span></div>
          <span className="badge bg-secondary rounded-pill fw-normal px-2 py-1">Did customer rate the order?</span>
        </div>
      </div>
      <div className="d-flex w-100 justify-content-center position-relative" style={{ maxWidth: '28rem' }}>
        <div className="w-50 border-top border-start border-secondary mt-4 position-relative" style={{ height: '2rem', borderTopLeftRadius: '0.5rem' }}>
          <div className="position-absolute top-0 start-0 translate-middle bg-white px-2 small fw-bold text-success d-flex align-items-center gap-1"><CheckCircle2 size={14}/> YES</div>
        </div>
        <div className="w-50 border-top border-end border-secondary mt-4 position-relative" style={{ height: '2rem', borderTopRightRadius: '0.5rem' }}>
          <div className="position-absolute top-0 end-0 translate-middle bg-white px-2 small fw-bold text-danger d-flex align-items-center gap-1"><XCircle size={14}/> NO</div>
        </div>
      </div>
      <div className="d-flex w-100 justify-content-center gap-4" style={{ maxWidth: '42rem' }}>
        <div className="flex-grow-1 card border-success-subtle bg-success-subtle rounded-4 p-3 shadow-sm position-relative" style={{ maxWidth: 280 }}>
          <div className="position-absolute top-0 start-50 translate-middle bg-success text-white fw-bold px-2 py-1 rounded small text-uppercase shadow-sm" style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}>Surprise Reward</div>
          <div className="d-flex align-items-start gap-2 mt-3">
            <ThumbsUp size={18} className="text-success flex-shrink-0 mt-1" />
            <div>
              <h6 className="small fw-semibold mb-0" style={{ color: 'var(--text-primary)' }}>Deposit 5% Cashback</h6>
              <p className="mb-0" style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>Reward positive behavior instantly.</p>
            </div>
          </div>
        </div>
        <div className="flex-grow-1 card border rounded-4 p-3 shadow-sm" style={{ maxWidth: 320 }}>
          <div className="d-flex align-items-start gap-2">
            <MessageSquare size={18} className="text-secondary flex-shrink-0 mt-1" />
            <div>
              <h6 className="small fw-semibold mb-0" style={{ color: 'var(--text-primary)' }}>Send Review Request SMS</h6>
              <div className="bg-light border p-2 rounded mt-1 fst-italic" style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                "How was your food? Rate your order to unlock a surprise wallet reward! [Link]"
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  if (type === 'Birthday') return (
    <>
      <div className="card rounded-4 p-3 shadow-sm w-100" style={{ maxWidth: '28rem', background: 'linear-gradient(135deg, #ec4899, #a855f7)', color: 'white' }}>
        <div className="d-flex align-items-start gap-3">
          <div className="bg-white text-dark p-2 rounded-circle mt-1"><Cake size={20} /></div>
          <div>
            <span className="small fw-bold text-uppercase" style={{ letterSpacing: '0.05em', fontSize: '0.7rem', color: 'rgba(255,255,255,0.8)' }}>Trigger Event</span>
            <h6 className="fw-semibold mb-1 text-white">Date Matches Birthday</h6>
            <p className="small mb-0" style={{ color: 'rgba(255,255,255,0.7)' }}>Today matches the birth date on the customer's profile.</p>
          </div>
        </div>
      </div>
      <Connector />
      <div className="card border-success-subtle bg-success-subtle rounded-4 p-3 shadow-sm position-relative w-100" style={{ maxWidth: '28rem' }}>
        <div className="position-absolute top-0 start-50 translate-middle bg-success text-white fw-bold px-2 py-1 rounded small text-uppercase shadow-sm" style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}>VIP Treatment</div>
        <div className="d-flex flex-column gap-3 mt-3">
          <div className="d-flex align-items-start gap-2">
            <Gift size={18} className="text-success flex-shrink-0 mt-1" />
            <div>
              <h6 className="small fw-semibold mb-0" style={{ color: 'var(--text-primary)' }}>Deposit ₹200 Birthday Credit</h6>
              <p className="mb-0" style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>High perceived value. Valid for Birthday Week.</p>
            </div>
          </div>
          <div className="d-flex align-items-start gap-2 pt-2 border-top border-success-subtle">
            <MessageSquare size={18} className="text-success flex-shrink-0 mt-1" />
            <div>
              <h6 className="small fw-semibold mb-0" style={{ color: 'var(--text-primary)' }}>Send Celebration SMS/Push</h6>
              <div className="bg-white border border-success-subtle p-2 rounded mt-1 fst-italic" style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                "Happy Birthday from ZamZam! Enjoy ₹200 off your celebration meal. Valid all week long!"
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return null;
}

// ─── Screen 3: Gamification ───────────────────────────────────────
function GamificationEngine() {
  return (
    <div className="d-flex flex-column gap-4">
      <div className="d-flex justify-content-between align-items-center">
        <div>
          <h5 className="fw-semibold mb-0" style={{ color: 'var(--text-primary)' }}>Gamification & Behavioral Challenges</h5>
          <p className="small mb-0" style={{ color: 'var(--text-secondary)' }}>Set up habit-forming mechanics that reward specific customer behaviors.</p>
        </div>
        <button className="btn btn-primary d-flex align-items-center gap-2 fw-medium">
          <Plus size={16} /> New Challenge
        </button>
      </div>
      <div className="row g-4">
        {/* Milestone Tracker */}
        <div className="col-md-6">
          <div className="card shadow-sm border-0 rounded-4 p-4 h-100 d-flex flex-column">
            <div className="d-flex align-items-center justify-content-between mb-3 pb-3 border-bottom">
              <div className="d-flex align-items-center gap-3">
                <div className="bg-warning-subtle text-warning p-2 rounded-3"><Trophy size={20} /></div>
                <h6 className="fw-semibold mb-0" style={{ color: 'var(--text-primary)' }}>Purchase Milestone Tracker</h6>
              </div>
              <span className="badge bg-success-subtle text-success fw-bold px-2 py-1 rounded">ACTIVE</span>
            </div>
            <div className="d-flex flex-column gap-3 flex-grow-1">
              <p className="small mb-0" style={{ color: 'var(--text-secondary)' }}>Reward customers for hitting a target number of orders to foster a sense of progress.</p>
              <div className="bg-light p-3 rounded-3 border d-flex flex-column gap-3">
                <div>
                  <label className="fw-bold text-uppercase d-block mb-1" style={{ fontSize: '0.7rem', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>Configuration</label>
                  <div className="d-flex align-items-center gap-2 small" style={{ color: 'var(--text-primary)' }}>
                    Complete <input type="number" defaultValue="5" className="form-control form-control-sm text-center fw-bold text-primary" style={{ width: 60 }} /> repeat orders
                  </div>
                </div>
                <div>
                  <label className="fw-bold text-uppercase d-block mb-1" style={{ fontSize: '0.7rem', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>Reward</label>
                  <div className="d-flex align-items-center gap-2 small" style={{ color: 'var(--text-primary)' }}>
                    Auto-deposit <input type="number" defaultValue="100" className="form-control form-control-sm text-center fw-bold text-success" style={{ width: 80 }} /> ₹ Credit
                  </div>
                </div>
                <div className="pt-2 form-check">
                  <input className="form-check-input" type="checkbox" defaultChecked id="vipBadge" />
                  <label className="form-check-label small" htmlFor="vipBadge" style={{ color: 'var(--text-secondary)' }}>Unlock Digital "VIP Badge" on profile</label>
                </div>
              </div>
            </div>
            <button className="btn btn-light fw-medium w-100 mt-3 border" style={{ color: 'var(--text-primary)' }}>Save Milestone Rules</button>
          </div>
        </div>

        {/* Cross-Selling Missions */}
        <div className="col-md-6">
          <div className="card shadow-sm border-0 rounded-4 p-4 h-100 d-flex flex-column">
            <div className="d-flex align-items-center justify-content-between mb-3 pb-3 border-bottom">
              <div className="d-flex align-items-center gap-3">
                <div className="p-2 rounded-3 bg-primary-subtle text-primary"><Target size={20} /></div>
                <h6 className="fw-semibold mb-0" style={{ color: 'var(--text-primary)' }}>Cross-Selling Missions</h6>
              </div>
              <span className="badge bg-success-subtle text-success fw-bold px-2 py-1 rounded">ACTIVE</span>
            </div>
            <div className="d-flex flex-column gap-3 flex-grow-1">
              <p className="small mb-0" style={{ color: 'var(--text-secondary)' }}>Design challenges to push specific menu items, clear inventory, or protect margins.</p>
              <div className="bg-light p-3 rounded-3 border d-flex flex-column gap-3">
                <div>
                  <label className="fw-bold text-uppercase d-block mb-1" style={{ fontSize: '0.7rem', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>Challenge Rule</label>
                  <div className="d-flex align-items-center gap-2 small" style={{ color: 'var(--text-primary)' }}>
                    Order a <select defaultValue="Dessert" className="form-select form-select-sm fw-bold text-primary w-auto"><option value="Dessert">Dessert</option><option value="Beverage">Beverage</option></select> with your main meal
                  </div>
                </div>
                <div>
                  <label className="fw-bold text-uppercase d-block mb-1" style={{ fontSize: '0.7rem', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>Reward (Margin Protected)</label>
                  <div className="d-flex align-items-center gap-2 small" style={{ color: 'var(--text-primary)' }}>
                    Unlock a <input type="number" defaultValue="5" className="form-control form-control-sm text-center fw-bold text-success" style={{ width: 60 }} /> % cashback credit for next time
                  </div>
                </div>
              </div>
            </div>
            <button className="btn btn-light fw-medium w-100 mt-3 border" style={{ color: 'var(--text-primary)' }}>Save Mission Rules</button>
          </div>
        </div>

        {/* Refer & Earn */}
        <div className="col-12">
          <div className="card shadow-sm border-0 rounded-4 p-4 d-flex flex-column">
            <div className="d-flex align-items-center justify-content-between mb-3 pb-3 border-bottom">
              <div className="d-flex align-items-center gap-3">
                <div className="bg-primary-subtle text-primary p-2 rounded-3"><Share2 size={20} /></div>
                <h6 className="fw-semibold mb-0" style={{ color: 'var(--text-primary)' }}>"Refer & Earn" Program Controls</h6>
              </div>
              <span className="badge bg-secondary-subtle text-secondary fw-bold px-2 py-1 rounded">DRAFT</span>
            </div>
            <div className="row g-4 align-items-start">
              <div className="col-md-6 d-flex flex-column gap-2">
                <p className="small mb-0" style={{ color: 'var(--text-secondary)' }}>Fuel word-of-mouth growth by rewarding social sharing. Credits are only released upon successful conversion.</p>
                <div className="alert alert-primary d-flex align-items-start gap-2 py-2 px-3 mb-0" style={{ fontSize: '0.8rem' }}>
                  <AlertCircle size={16} className="mt-1 flex-shrink-0" />
                  <p className="mb-0">Trigger Condition: Credits are <strong>automatically deposited</strong> only after the new user successfully completes their first app order.</p>
                </div>
              </div>
              <div className="col-md-6">
                <div className="row g-3">
                  {[{ label: 'Referrer Reward', id: 'ref1' }, { label: 'Referee Reward', id: 'ref2' }].map(({ label, id }) => (
                    <div className="col-sm-6" key={id}>
                      <div className="bg-light p-3 rounded-3 border">
                        <label className="fw-bold text-uppercase d-block mb-2" style={{ fontSize: '0.7rem', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>{label}</label>
                        <div className="input-group">
                          <span className="input-group-text bg-white">₹</span>
                          <input type="number" className="form-control fw-bold" defaultValue="50" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="d-flex justify-content-end mt-4 pt-3 border-top">
              <button className="btn btn-primary fw-medium px-4">Activate Referral Program</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Screen 4: Permissions ────────────────────────────────────────
function PermissionsControl() {
  return (
    <div className="row g-4">
      <div className="col-12">
        <h5 className="fw-semibold mb-0" style={{ color: 'var(--text-primary)' }}>Staff Distribution & Permissions</h5>
        <p className="small mb-0" style={{ color: 'var(--text-secondary)' }}>Control who can manually issue promotional credits and set hard caps.</p>
      </div>

      <div className="col-lg-8 d-flex flex-column gap-4">
        <div className="data-table-wrapper">
          <div className="p-3 border-bottom bg-light d-flex justify-content-between align-items-center">
            <h6 className="fw-semibold mb-0 d-flex align-items-center gap-2" style={{ color: 'var(--text-primary)' }}>
              <ShieldCheck size={16} className="text-primary" /> Role-Based Credit Limits
            </h6>
          </div>
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>System Role</th>
                  <th>Max Issue Cap</th>
                  <th>Approval Required</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="fw-medium d-flex align-items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                    <div className="rounded bg-secondary-subtle d-flex align-items-center justify-content-center" style={{ width: 32, height: 32 }}><Users size={14} className="text-secondary"/></div>
                    Call Center Agent
                  </td>
                  <td><span className="badge border border-secondary text-secondary bg-light px-2 py-1 fw-medium">5% Discount</span></td>
                  <td style={{ color: 'var(--text-secondary)' }}>No</td>
                </tr>
                <tr>
                  <td className="fw-medium d-flex align-items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                    <div className="rounded bg-primary-subtle d-flex align-items-center justify-content-center" style={{ width: 32, height: 32 }}><ShieldCheck size={14} className="text-primary"/></div>
                    Shift Manager
                  </td>
                  <td><span className="badge border border-primary-subtle text-primary bg-primary-subtle px-2 py-1 fw-medium">10% Discount</span></td>
                  <td style={{ color: 'var(--text-secondary)' }}>No</td>
                </tr>
                <tr>
                  <td className="fw-medium d-flex align-items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                    <div className="rounded bg-info-subtle d-flex align-items-center justify-content-center" style={{ width: 32, height: 32 }}><AlertCircle size={14} className="text-info-emphasis"/></div>
                    Administrator
                  </td>
                  <td><span className="badge border border-info-subtle text-info-emphasis bg-info-subtle px-2 py-1 fw-medium">Unlimited (Override)</span></td>
                  <td style={{ color: 'var(--text-secondary)' }}>—</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="col-lg-4">
        <div className="card shadow-sm border-0 rounded-4 p-4">
          <h6 className="fw-semibold mb-3 pb-2 border-bottom d-flex align-items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <Gift size={16} className="text-success" /> Manual Credit Push
          </h6>
          <div className="d-flex flex-column gap-3">
            <div>
              <label className="form-label">Customer Phone / ID</label>
              <input type="text" placeholder="+91 98765 43210" className="form-control" />
            </div>
            <div>
              <label className="form-label">Credit Amount</label>
              <div className="input-group">
                <input type="number" placeholder="50" className="form-control" />
                <select defaultValue="₹" className="form-select bg-light" style={{ maxWidth: 80 }}>
                  <option value="₹">₹</option>
                  <option value="%">%</option>
                </select>
              </div>
            </div>
            <div>
              <label className="form-label">Reason Tag (Required)</label>
              <select defaultValue="" className="form-select">
                <option value="" disabled>Select a reason...</option>
                <option value="Apology">Apology / Issue Resolution</option>
                <option value="First-time">First-time Caller Incentive</option>
                <option value="Loyalty">Loyalty Reward (Manual)</option>
                <option value="Admin">Admin Override / Promo</option>
              </select>
            </div>
            <div>
              <label className="form-label">Internal Note</label>
              <textarea rows="2" placeholder="Late delivery complaint..." className="form-control" style={{ resize: 'none' }}></textarea>
            </div>
            <button className="btn btn-dark w-100 fw-medium mt-2 py-2">Issue Credit</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Screen 5: Strategic Reporting ───────────────────────────────
function StrategicReporting() {
  return (
    <div className="d-flex flex-column gap-4">
      <div className="row g-4">
        <div className="col-lg-4">
          <h5 className="fw-semibold mb-0" style={{ color: 'var(--text-primary)' }}>Strategic Outcome Report</h5>
          <p className="small mb-0" style={{ color: 'var(--text-secondary)' }}>Measure actual ROI and strategic success of wallet spend.</p>
          <div className="mt-3 d-flex gap-2">
            <button className="btn btn-outline-secondary btn-sm fw-medium px-3 bg-white shadow-sm">Last 30 Days</button>
            <button className="btn btn-primary btn-sm fw-medium px-3 shadow-sm">Export Report</button>
          </div>
        </div>
        <div className="col-lg-8">
          <div className="card border-primary-subtle bg-primary-subtle rounded-4 p-4 shadow-sm position-relative overflow-hidden h-100">
            <div className="position-absolute top-0 end-0 bg-primary opacity-10 rounded-circle" style={{ width: 150, height: 150, filter: 'blur(30px)', marginRight: -30, marginTop: -30 }}></div>
            <div className="d-flex gap-3 position-relative" style={{ zIndex: 1 }}>
              <div className="bg-primary text-white p-2 rounded-3 align-self-start"><Sparkles size={20} /></div>
              <div>
                <h6 className="small fw-bold text-primary-emphasis mb-1">AI Improvisation Insight</h6>
                <p className="small text-primary-emphasis mb-2 lh-base">
                  The <strong className="fw-semibold bg-white bg-opacity-50 px-1 rounded">"Refer & Earn"</strong> campaign has the highest ROI (4x), but the <strong className="fw-semibold bg-white bg-opacity-50 px-1 rounded">"Cross-Sell Dessert"</strong> challenge has a low 2% redemption rate.
                </p>
                <div className="d-flex gap-2 mt-2">
                  <button className="btn btn-sm btn-light text-primary fw-semibold shadow-sm px-3" style={{ fontSize: '0.75rem' }}>Increase Dessert Reward to 10%</button>
                  <button className="btn btn-sm btn-outline-primary border-0 fw-semibold px-3" style={{ fontSize: '0.75rem' }}>Pause Campaign</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3 mt-1">
        {[
          { label: 'Total Issued',       value: '₹85,400', sub: 'Lifetime Liability',     theme: 'bg-white',          icon: <IndianRupee size={16} className="text-secondary" />,             dark: false },
          { label: 'Redeemed (Actual)',  value: '₹32,100', sub: '37.5% Redemption Rate',  theme: 'bg-success-subtle border border-success-subtle', icon: <CheckCircle2 size={16} className="text-success" />,  dark: false },
          { label: 'Expired (Saved)',    value: '₹41,200', sub: 'Zero Financial Impact',  theme: 'bg-white border border-secondary-subtle', icon: <Clock size={16} className="text-secondary" />,       dark: false },
          { label: 'Overall ROI',        value: '4.2x',    sub: 'Revenue vs Promo Cost',  theme: 'bg-dark',            icon: <TrendingUp size={16} className="text-light opacity-75" />,        dark: true  },
        ].map(({ label, value, sub, theme, icon, dark }) => (
          <div className="col-6 col-md-3" key={label}>
            <div className={`card border-0 shadow-sm rounded-4 p-3 ${theme}`}>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className={`fw-bold text-uppercase ${dark ? 'text-light opacity-75' : 'text-secondary'}`} style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}>{label}</span>
                {icon}
              </div>
              <h3 className={`fs-3 fw-bold mb-2 ${dark ? 'text-white' : ''}`} style={dark ? {} : { color: 'var(--text-primary)' }}>{value}</h3>
              <span className={`badge fw-medium align-self-start ${dark ? 'bg-secondary text-white border border-secondary' : 'bg-light text-secondary border'}`} style={{ fontSize: '0.7rem' }}>{sub}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="row g-4 mt-2">
        <div className="col-lg-4">
          <div className="card shadow-sm border-0 rounded-4 p-4 h-100 d-flex flex-column">
            <h6 className="fw-semibold mb-4 d-flex align-items-center gap-2" style={{ color: 'var(--text-primary)' }}>
              <PieChart size={16} className="text-secondary"/> Wallet Spend by Type
            </h6>
            <div className="progress mb-4 rounded-3 shadow-sm" style={{ height: '2rem' }}>
              <div className="progress-bar bg-primary"   style={{ width: '35%' }}></div>
              <div className="progress-bar bg-info"      style={{ width: '25%' }}></div>
              <div className="progress-bar"              style={{ width: '20%', background: 'var(--bs-purple)' }}></div>
              <div className="progress-bar bg-success"   style={{ width: '15%' }}></div>
              <div className="progress-bar bg-secondary" style={{ width: '5%'  }}></div>
            </div>
            <div className="d-flex flex-column gap-2 flex-grow-1">
              {[
                { color: 'bg-primary',   bg: null,                title: 'Automated Nudges (Conv.)', pct: '35%', val: '₹15,820' },
                { color: 'bg-info',      bg: null,                title: 'Refer & Earn (Acq.)',       pct: '25%', val: '₹11,300' },
                { color: '',             bg: 'var(--bs-purple)', title: 'Milestones (Retention)',     pct: '20%', val: '₹9,040'  },
                { color: 'bg-success',   bg: null,                title: 'Cross-Sell (AOV Lift)',      pct: '15%', val: '₹6,780'  },
                { color: 'bg-secondary', bg: null,                title: 'Manual Credits (Recovery)', pct: '5%',  val: '₹2,260'  },
              ].map(({ color, bg, title, pct, val }) => (
                <div className="d-flex align-items-center justify-content-between small" key={title}>
                  <div className="d-flex align-items-center gap-2">
                    <div className={`rounded-1 ${color}`} style={{ width: 12, height: 12, backgroundColor: bg || undefined }}></div>
                    <span className="fw-medium" style={{ color: 'var(--text-primary)' }}>{title}</span>
                  </div>
                  <div className="d-flex align-items-center gap-3 text-end">
                    <span style={{ color: 'var(--text-secondary)', width: 30 }}>{pct}</span>
                    <span className="fw-semibold" style={{ color: 'var(--text-primary)', width: 55 }}>{val}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="pt-3 mt-3 border-top d-flex justify-content-between align-items-end">
              <span className="fw-bold text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>Total Promo Spend</span>
              <span className="fs-4 fw-bold" style={{ color: 'var(--text-primary)' }}>₹45,200</span>
            </div>
          </div>
        </div>

        <div className="col-lg-8">
          <div className="row g-3 h-100">
            {[
              { goal: 'Goal 1: CAC Efficiency',    metric: '₹110',  metricLabel: 'Cost per New App User',       description: 'Comparing cost of "Refer & Earn" + "First-Time" credits against new revenue generated.',  trend: 'Good',     icon: <Target size={24} className="text-primary"/> },
              { goal: 'Goal 2: Increasing AOV',    metric: '+₹185', metricLabel: 'Lift per Ticket vs Non-Wallet', description: 'Tracking if customers doing "Cross-Sell Challenges" spend more overall.',                  trend: 'Excellent', icon: <ShoppingBag size={24} className="text-success"/> },
              { goal: 'Goal 3: Repeat Frequency',  metric: '3.2x',  metricLabel: 'Orders per Month',             description: 'Users participating in Gamified Milestones order 3.2x more often than average.',           trend: 'Excellent', icon: <Workflow size={24} style={{ color: 'var(--bs-purple)' }}/> },
              { goal: 'Goal 4: Margin Protection', metric: '28%',   metricLabel: 'Net Profit Margin on Promos',  description: 'Ensuring wallet discounts drive enough volume on high-margin items to justify the cost.',   trend: 'Warning',   icon: <TrendingUp size={24} className="text-warning"/> },
            ].map(({ goal, metric, metricLabel, description, trend, icon }) => {
              const badgeClass = { Excellent: 'bg-success-subtle text-success', Good: 'bg-primary-subtle text-primary', Warning: 'bg-warning-subtle text-warning-emphasis' }[trend];
              return (
                <div className="col-sm-6" key={goal}>
                  <div className="card border bg-white rounded-4 p-4 shadow-sm h-100 d-flex flex-column justify-content-between">
                    <div>
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <h6 className="fw-bold text-uppercase mb-0" style={{ fontSize: '0.7rem', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>{goal}</h6>
                        <span className={`badge rounded px-2 py-1 fw-bold text-uppercase ${badgeClass}`} style={{ fontSize: '0.65rem' }}>{trend}</span>
                      </div>
                      <div className="d-flex align-items-center gap-3 mb-2">
                        <div className="bg-light p-2 rounded-3">{icon}</div>
                        <h2 className="fs-3 fw-bold mb-0" style={{ color: 'var(--text-primary)' }}>{metric}</h2>
                      </div>
                      <p className="small fw-medium mb-0" style={{ color: 'var(--text-primary)' }}>{metricLabel}</p>
                    </div>
                    <div className="mt-3 pt-3 border-top">
                      <p className="mb-0" style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', lineHeight: 1.5 }}>{description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
